import { useEffect, useMemo, useState } from 'react'
import EmpresaLayout from '@/components/EmpresaLayout'
import InterviewFormModal from '@/components/InterviewFormModal'
import { useAuth } from '@/hooks/useAuth'
import { useInterviews, type Interview } from '@/hooks/useInterviews'
import { useInterviewTemplates } from '@/hooks/useInterviewTemplates'
import { useApplications } from '@/hooks/useApplications'
import { useTalentBank } from '@/hooks/useTalentBank'
import { useExpressCandidates, type ExpressCandidate } from '@/hooks/useExpressCandidates'
import {
  RESULT_LABELS,
  RESULT_STYLES,
  INTERVIEW_PROC_LABELS,
  INTERVIEW_PROC_STYLES,
} from '@/lib/recruitment'
import { lumi } from '@/lib/lumi'
import {Plus, Trash2, Edit2, ClipboardList, X, Star, UserPlus, GitCompare, History, Calendar, Building2, } from 'lucide-react'

function EmpresaEntrevistas() {
  const { user } = useAuth()
  const { interviews, loading, fetchByCompany, createInterview, updateInterview, deleteInterview } =
    useInterviews()
  const { templates, fetchByCompany: fetchTemplates } = useInterviewTemplates()
  const { applications, fetchByCompany: fetchApps } = useApplications()
  const { addEntry, updateEntry } = useTalentBank()
  const {
    candidates: expressCandidates,
    fetchByCompany: fetchExpress,
    createCandidate: createExpressCandidate,
  } = useExpressCandidates()

  const [editingInterview, setEditingInterview] = useState<Interview | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [compareMode, setCompareMode] = useState(false)
  const [compareIds, setCompareIds] = useState<string[]>([])
  const [historyCandidate, setHistoryCandidate] = useState<{ id: string; name: string } | null>(null)
  const [companyTradeName, setCompanyTradeName] = useState('')

  const refresh = () => {
    if (user?.userId) fetchByCompany(user.userId)
  }

  useEffect(() => {
    if (user?.userId) {
      fetchByCompany(user.userId)
      fetchTemplates(user.userId)
      fetchApps(user.userId)
      fetchExpress(user.userId)
      // Cargar el nombre comercial real de la empresa
      lumi.entities.companies
        .list({ filter: { userId: user.userId }, limit: 1 })
        .then((r) => {
          if (r.list && r.list.length > 0) {
            setCompanyTradeName((r.list[0] as any).tradeName || user.email || '')
          } else {
            setCompanyTradeName(user.email || '')
          }
        })
        .catch(() => setCompanyTradeName(user.email || ''))
    }
  }, [user, fetchByCompany, fetchTemplates, fetchApps, fetchExpress])

  const activeTemplates = useMemo(() => templates.filter((t) => t.active !== false), [templates])

  const templateById = useMemo(() => {
    const map: Record<string, (typeof templates)[number]> = {}
    templates.forEach((t) => (map[t._id] = t))
    return map
  }, [templates])

  const handleSaveInterview = async (data: Partial<Interview>) => {
    if (!editingInterview) return
    try {
      await updateInterview(editingInterview._id, data)
      setEditingInterview(null)
      refresh()
    } catch (e) {
      console.error('Error al guardar entrevista:', e)
      alert('No se pudo guardar la entrevista.')
    }
  }

  const handleDelete = async (it: Interview) => {
    if (!confirm(`¿Eliminar la entrevista de "${it.collaboratorName}"?`)) return
    try {
      await deleteInterview(it._id)
      refresh()
    } catch (e) {
      console.error('Error al eliminar entrevista:', e)
    }
  }

  const sendToTalentBank = async (it: Interview) => {
    if (!user?.userId) return
    try {
      // Prioriza la foto capturada durante la entrevista (cámara/archivo);
      // si no existe, usa la foto de perfil del colaborador.
      const interviewPhoto = (it as Record<string, unknown>)['candidatePhoto'] as string | undefined
      const photo = interviewPhoto || it.collaboratorPhoto || ''

      // Si el candidato ya existe en el banco de esta empresa, actualiza su foto
      // y últimos datos en lugar de crear un duplicado sin imagen.
      const { list: existing } = await lumi.entities.talent_bank.list({
        filter: { companyUserId: user.userId, collaboratorUserId: it.collaboratorUserId },
      })
      if (existing && existing.length > 0) {
        await updateEntry(existing[0]._id, {
          collaboratorName: it.collaboratorName,
          collaboratorEmail: it.collaboratorEmail,
          ...(photo ? { collaboratorPhoto: photo } : {}),
          lastVacancyTitle: it.vacancyTitle,
          lastScore: it.overallScore,
        })
        alert(`Se actualizó a ${it.collaboratorName} en el banco de talento.`)
        return
      }

      await addEntry({
        companyUserId: user.userId,
        companyName: companyTradeName || user.email || '',
        collaboratorUserId: it.collaboratorUserId,
        collaboratorName: it.collaboratorName,
        collaboratorEmail: it.collaboratorEmail,
        collaboratorPhoto: photo,
        lastVacancyTitle: it.vacancyTitle,
        lastScore: it.overallScore,
        classification: 'Por clasificar',
      })
      alert(`${it.collaboratorName} fue agregado al banco de talento.`)
    } catch (e) {
      console.error('Error al enviar al banco de talento:', e)
      alert('No se pudo agregar al banco de talento.')
    }
  }

  const toggleCompare = (id: string) => {
    setCompareIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 4 ? [...prev, id] : prev,
    )
  }

  const compareList = useMemo(
    () => interviews.filter((i) => compareIds.includes(i._id)),
    [interviews, compareIds],
  )

  return (
    <EmpresaLayout
      title="Entrevistas"
      subtitle="Documenta entrevistas, evalúa candidatos y toma mejores decisiones de contratación"
    >
      <div className="mb-5 flex flex-wrap items-center justify-end gap-2">
        <button
          onClick={() => {
            setCompareMode((v) => !v)
            setCompareIds([])
          }}
          className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold ${
            compareMode
              ? 'border-amber-400 bg-amber-50 text-amber-700'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <GitCompare size={16} /> Comparar candidatos
        </button>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus size={16} /> Nueva entrevista
        </button>
      </div>

      {compareMode && compareList.length >= 2 && (
        <ComparatorTable
          interviews={compareList}
          templateById={templateById}
          onClose={() => {
            setCompareMode(false)
            setCompareIds([])
          }}
        />
      )}

      {loading ? (
        <div className="py-16 text-center text-gray-400">Cargando entrevistas...</div>
      ) : interviews.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
          <ClipboardList className="mx-auto mb-3 text-gray-300" size={40} />
          <p className="font-semibold text-gray-700">Aún no hay entrevistas registradas</p>
          <p className="mt-1 text-sm text-gray-400">
            Crea una entrevista desde un postulante para empezar a documentar tu proceso.
          </p>
        </div>
      ) : (
        <>
          {/* ── Tarjetas móvil (< md) ── */}
          <div className="space-y-3 md:hidden">
            {interviews.map((it) => (
              <div key={it._id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                {/* Cabecera: checkbox comparar + candidato */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    {compareMode && (
                      <input
                        type="checkbox"
                        checked={compareIds.includes(it._id)}
                        onChange={() => toggleCompare(it._id)}
                        className="mt-1 rounded border-gray-300"
                      />
                    )}
                    <div>
                      <button
                        type="button"
                        onClick={() => setHistoryCandidate({ id: it.collaboratorUserId, name: it.collaboratorName || 'Candidato' })}
                        className="text-left"
                      >
                        <p className="font-bold text-gray-900 hover:text-amber-600">{it.collaboratorName || '—'}</p>
                      </button>
                      {it.collaboratorEmail ? (
                        <p className="text-xs text-gray-400">{it.collaboratorEmail}</p>
                      ) : null}
                    </div>
                  </div>
                  {/* Acciones compactas */}
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => setEditingInterview(it)}
                      className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
                      title="Evaluar / editar"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => sendToTalentBank(it)}
                      className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-violet-50 hover:text-violet-600"
                      title="Banco de talento"
                    >
                      <UserPlus size={15} />
                    </button>
                    <button
                      onClick={() => setHistoryCandidate({ id: it.collaboratorUserId, name: it.collaboratorName || 'Candidato' })}
                      className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-blue-50 hover:text-blue-600"
                      title="Historial"
                    >
                      <History size={15} />
                    </button>
                    <button
                      onClick={() => handleDelete(it)}
                      className="rounded-lg border border-red-200 p-2 text-red-500 hover:bg-red-50"
                      title="Eliminar"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* Datos en grid 2 columnas */}
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-gray-100 pt-3 text-sm">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Vacante</p>
                    <p className="mt-0.5 text-gray-700">{it.vacancyTitle || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Estado</p>
                    <div className="mt-0.5">
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                        INTERVIEW_PROC_STYLES[it.status] || 'bg-gray-100 text-gray-600'
                      }`}>
                        {INTERVIEW_PROC_LABELS[it.status] || it.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Calificación</p>
                    <div className="mt-0.5">
                      {it.overallScore ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-gray-800">
                          <Star size={12} className="text-amber-400" />
                          {it.overallScore}/{it.scaleType || '10'}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Resultado</p>
                    <div className="mt-0.5">
                      {it.result ? (
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${RESULT_STYLES[it.result]}`}>
                          {RESULT_LABELS[it.result]}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Tabla desktop (≥ md) ── */}
          <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs uppercase text-gray-500">
                  {compareMode && <th className="px-4 py-3"></th>}
                  <th className="px-4 py-3">Candidato</th>
                  <th className="px-4 py-3">Vacante</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Calificación</th>
                  <th className="px-4 py-3">Resultado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {interviews.map((it) => (
                  <tr key={it._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                    {compareMode && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={compareIds.includes(it._id)}
                          onChange={() => toggleCompare(it._id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setHistoryCandidate({ id: it.collaboratorUserId, name: it.collaboratorName || 'Candidato' })}
                        className="text-left"
                        title="Ver historial de entrevistas"
                      >
                        <div className="font-semibold text-gray-900 hover:text-amber-600 hover:underline">{it.collaboratorName}</div>
                        <div className="text-xs text-gray-400">{it.collaboratorEmail}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{it.vacancyTitle || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                        INTERVIEW_PROC_STYLES[it.status] || 'bg-gray-100 text-gray-600'
                      }`}>
                        {INTERVIEW_PROC_LABELS[it.status] || it.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {it.overallScore ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-gray-800">
                          <Star size={13} className="text-amber-400" />
                          {it.overallScore}/{it.scaleType || '10'}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {it.result ? (
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${RESULT_STYLES[it.result]}`}>
                          {RESULT_LABELS[it.result]}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setEditingInterview(it)}
                          className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50"
                          title="Evaluar / editar"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => sendToTalentBank(it)}
                          className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-violet-50 hover:text-violet-600"
                          title="Enviar a banco de talento"
                        >
                          <UserPlus size={14} />
                        </button>
                        <button
                          onClick={() => setHistoryCandidate({ id: it.collaboratorUserId, name: it.collaboratorName || 'Candidato' })}
                          className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-blue-50 hover:text-blue-600"
                          title="Historial de entrevistas previas"
                        >
                          <History size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(it)}
                          className="rounded-lg border border-red-200 p-1.5 text-red-500 hover:bg-red-50"
                          title="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal nueva entrevista desde postulante o candidato express */}
      {showNew && (
        <NewInterviewModal
          applications={applications}
          templates={activeTemplates}
          companyUserId={user?.userId || ''}
          companyName={companyTradeName || user?.email || ''}
          createExpressCandidate={createExpressCandidate}
          onClose={() => setShowNew(false)}
          onCreate={async (payload) => {
            if (!user?.userId) return
            try {
              await createInterview({
                ...payload,
                companyUserId: user.userId,
                companyName: companyTradeName || user.email || '',
              })
              setShowNew(false)
              refresh()
              fetchExpress(user.userId)
            } catch (e) {
              console.error('Error al crear entrevista:', e)
              alert('No se pudo crear la entrevista.')
            }
          }}
        />
      )}

      {/* Modal evaluación */}
      {editingInterview && (
        <InterviewFormModal
          interview={editingInterview}
          template={editingInterview.templateId ? templateById[editingInterview.templateId] : undefined}
          onClose={() => setEditingInterview(null)}
          onSave={handleSaveInterview}
        />
      )}

      {/* Modal historial de entrevistas previas del candidato */}
      {historyCandidate && (
        <CandidateHistoryModal
          candidateId={historyCandidate.id}
          candidateName={historyCandidate.name}
          interviews={interviews}
          templateById={templateById}
          onClose={() => setHistoryCandidate(null)}
        />
      )}
    </EmpresaLayout>
  )
}

/* ---------- Modal: nueva entrevista desde postulante o candidato express ---------- */
function NewInterviewModal({
  applications,
  templates,
  companyUserId,
  companyName,
  createExpressCandidate,
  onClose,
  onCreate,
}: {
  applications: ReturnType<typeof useApplications>['applications']
  templates: ReturnType<typeof useInterviewTemplates>['templates']
  companyUserId: string
  companyName: string
  createExpressCandidate: (data: Partial<ExpressCandidate>) => Promise<any>
  onClose: () => void
  onCreate: (payload: Partial<Interview>) => void
}) {
  const [mode, setMode] = useState<'postulante' | 'express'>('postulante')
  const [appId, setAppId] = useState('')
  // Campos del candidato express
  const [exFullName, setExFullName] = useState('')
  const [exPhone, setExPhone] = useState('')
  const [exVacancy, setExVacancy] = useState('')
  // Comunes
  const [templateId, setTemplateId] = useState('')
  const [interviewer, setInterviewer] = useState('')
  const [scheduledDate, setScheduledDate] = useState('')
  const [photoMap, setPhotoMap] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  // Cargar fotos de colaboradores asociados a postulantes
  useEffect(() => {
    let active = true
    const ids = Array.from(new Set(applications.map((a) => a.collaboratorUserId).filter(Boolean)))
    if (ids.length === 0) return
    lumi.entities.collaborators
      .list({ filter: { userId: { $in: ids } } })
      .then((r) => {
        if (!active) return
        const map: Record<string, string> = {}
        ;(r.list || []).forEach((c: any) => {
          if (c.userId) map[c.userId] = c.photoUrl || ''
        })
        setPhotoMap(map)
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [applications])

  const submit = async () => {
    const tpl = templates.find((t) => t._id === templateId)

    if (mode === 'postulante') {
      const app = applications.find((a) => a._id === appId)
      if (!app) {
        alert('Selecciona un candidato.')
        return
      }
      onCreate({
        collaboratorUserId: app.collaboratorUserId,
        collaboratorName: app.collaboratorName,
        collaboratorEmail: app.collaboratorEmail,
        collaboratorPhoto: photoMap[app.collaboratorUserId] || '',
        vacancyId: app.vacancyId,
        vacancyTitle: app.vacancyTitle,
        applicationId: app._id,
        templateId: tpl?._id,
        templateName: tpl?.name,
        scaleType: (tpl?.scaleType || '10') as '10' | '100',
        interviewer,
        scheduledDate,
        status: 'programada',
      })
      return
    }

    // Modo express: registrar nuevo candidato
    if (!exFullName.trim()) {
      alert('Ingresa el nombre completo del candidato.')
      return
    }
    if (!exPhone.trim()) {
      alert('Ingresa el teléfono del candidato.')
      return
    }
    setSaving(true)
    try {
      const created = await createExpressCandidate({
        companyUserId,
        companyName,
        fullName: exFullName.trim(),
        phone: exPhone.trim(),
        source: 'express',
        notes: exVacancy.trim() ? `Vacante: ${exVacancy.trim()}` : '',
      })
      const expressId =
        created?._id || created?.data?._id || `express-${Date.now()}`
      onCreate({
        collaboratorUserId: expressId,
        collaboratorName: exFullName.trim(),
        collaboratorEmail: exPhone.trim(),
        collaboratorPhoto: '',
        vacancyTitle: exVacancy.trim(),
        templateId: tpl?._id,
        templateName: tpl?.name,
        scaleType: (tpl?.scaleType || '10') as '10' | '100',
        interviewer,
        scheduledDate,
        status: 'programada',
      })
    } catch (e) {
      console.error('Error al registrar candidato express:', e)
      alert('No se pudo registrar el candidato.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-lg font-bold text-gray-900">Nueva entrevista</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">
          {/* Selector de modo */}
          <div className="flex rounded-lg border border-gray-200 p-1">
            <button
              type="button"
              onClick={() => setMode('postulante')}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold ${
                mode === 'postulante' ? 'bg-slate-900 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Postulante existente
            </button>
            <button
              type="button"
              onClick={() => setMode('express')}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold ${
                mode === 'express' ? 'bg-slate-900 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Registrar nuevo candidato
            </button>
          </div>

          {mode === 'postulante' ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Candidato (postulante) *</label>
              <select
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
              >
                <option value="">Seleccionar postulante...</option>
                {applications.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.collaboratorName} — {a.vacancyTitle}
                  </option>
                ))}
              </select>
              {applications.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  No hay postulantes registrados todavía. Usa "Registrar nuevo candidato".
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-4">
              <p className="text-xs font-medium text-amber-700">
                Registra un candidato directamente para entrevistarlo.
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nombre completo *</label>
                <input
                  value={exFullName}
                  onChange={(e) => setExFullName(e.target.value)}
                  placeholder="Ej. Juan Pérez García"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Teléfono *</label>
                <input
                  value={exPhone}
                  onChange={(e) => setExPhone(e.target.value)}
                  placeholder="Ej. 222 123 4567"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Vacante (opcional)</label>
                <input
                  value={exVacancy}
                  onChange={(e) => setExVacancy(e.target.value)}
                  placeholder="Ej. Vendedor de piso"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Plantilla</label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
            >
              <option value="">Sin plantilla</option>
              {templates.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.name} ({t.category})
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Entrevistador</label>
              <input
                value={interviewer}
                onChange={(e) => setInterviewer(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Fecha</label>
              <input
                type="datetime-local"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="rounded-lg bg-amber-400 px-5 py-2 text-sm font-bold text-slate-900 hover:bg-amber-300 disabled:opacity-60"
          >
            {mode === 'express' ? (saving ? 'Guardando...' : 'Guardar y crear entrevista') : 'Crear entrevista'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ---------- Comparador de candidatos ---------- */
function ComparatorTable({
  interviews,
  templateById,
  onClose,
}: {
  interviews: Interview[]
  templateById: Record<string, any>
  onClose: () => void
}) {
  // Reúne todas las competencias presentes
  const allComps = useMemo(() => {
    const set = new Set<string>()
    interviews.forEach((i) => (i.competencyScores || []).forEach((c) => set.add(c.name)))
    return Array.from(set)
  }, [interviews])

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-amber-200 bg-amber-50/40 shadow-sm">
      <div className="flex items-center justify-between border-b border-amber-200 px-5 py-3">
        <h3 className="font-bold text-gray-900">Comparación de candidatos</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
          <X size={18} />
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500">
              <th className="px-4 py-3">Criterio</th>
              {interviews.map((i) => (
                <th key={i._id} className="px-4 py-3 text-gray-700">
                  {i.collaboratorName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            <tr className="border-t border-gray-100">
              <td className="px-4 py-2.5 font-medium text-gray-600">Calificación general</td>
              {interviews.map((i) => (
                <td key={i._id} className="px-4 py-2.5 font-semibold text-gray-900">
                  {i.overallScore ? `${i.overallScore}/${i.scaleType || '10'}` : '—'}
                </td>
              ))}
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-4 py-2.5 font-medium text-gray-600">Resultado</td>
              {interviews.map((i) => (
                <td key={i._id} className="px-4 py-2.5">
                  {i.result ? RESULT_LABELS[i.result] : '—'}
                </td>
              ))}
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-4 py-2.5 font-medium text-gray-600">Plantilla</td>
              {interviews.map((i) => (
                <td key={i._id} className="px-4 py-2.5 text-gray-600">
                  {i.templateId ? templateById[i.templateId]?.name || i.templateName : i.templateName || '—'}
                </td>
              ))}
            </tr>
            {allComps.map((comp) => (
              <tr key={comp} className="border-t border-gray-100">
                <td className="px-4 py-2.5 text-gray-600">{comp}</td>
                {interviews.map((i) => {
                  const found = (i.competencyScores || []).find((c) => c.name === comp)
                  return (
                    <td key={i._id} className="px-4 py-2.5 text-gray-800">
                      {found ? found.score : '—'}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ---------- Modal: historial de entrevistas previas del candidato ---------- */
function CandidateHistoryModal({
  candidateId,
  candidateName,
  interviews,
  templateById,
  onClose,
}: {
  candidateId: string
  candidateName: string
  interviews: Interview[]
  templateById: Record<string, any>
  onClose: () => void
}) {
  const history = useMemo(
    () =>
      interviews
        .filter((i) => i.collaboratorUserId === candidateId)
        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
    [interviews, candidateId],
  )

  const avg = useMemo(() => {
    const scored = history.filter((i) => typeof i.overallScore === 'number')
    if (scored.length === 0) return null
    return (scored.reduce((acc, i) => acc + (i.overallScore || 0), 0) / scored.length).toFixed(1)
  }, [history])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Historial de entrevistas</h2>
            <p className="text-sm text-gray-500">{candidateName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-wrap gap-3 border-b border-gray-100 px-6 py-3">
          <div className="rounded-lg bg-gray-50 px-3 py-1.5 text-sm">
            <span className="text-gray-500">Entrevistas: </span>
            <span className="font-bold text-gray-900">{history.length}</span>
          </div>
          <div className="rounded-lg bg-amber-50 px-3 py-1.5 text-sm">
            <span className="text-amber-700">Promedio general: </span>
            <span className="font-bold text-amber-800">{avg ?? '—'}</span>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-6 py-5">
          {history.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">Sin entrevistas previas.</p>
          ) : (
            history.map((it) => (
              <div key={it._id} className="rounded-xl border border-gray-200 p-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={13} className="text-gray-400" />
                    {it.scheduledDate
                      ? new Date(it.scheduledDate).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
                      : it.createdAt
                        ? new Date(it.createdAt).toLocaleDateString('es-MX', { dateStyle: 'medium' })
                        : '—'}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${INTERVIEW_PROC_STYLES[it.status] || 'bg-gray-100 text-gray-600'}`}>
                      {INTERVIEW_PROC_LABELS[it.status] || it.status}
                    </span>
                    {it.result && (
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${RESULT_STYLES[it.result]}`}>
                        {RESULT_LABELS[it.result]}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-700">
                  {it.vacancyTitle && (
                    <span className="inline-flex items-center gap-1">
                      <Building2 size={13} className="text-gray-400" /> {it.vacancyTitle}
                    </span>
                  )}
                  {it.interviewer && <span className="text-gray-500">Entrevistador: {it.interviewer}</span>}
                  {(it.templateName || (it.templateId && templateById[it.templateId]?.name)) && (
                    <span className="text-gray-500">
                      Plantilla: {it.templateName || templateById[it.templateId]?.name}
                    </span>
                  )}
                  {typeof it.overallScore === 'number' && (
                    <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                      <Star size={12} className="text-amber-400" /> {it.overallScore}/{it.scaleType || '10'}
                    </span>
                  )}
                </div>
                {(it.competencyScores || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(it.competencyScores || []).map((cs) => (
                      <span key={cs.name} className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                        {cs.name}: <span className="font-semibold">{cs.score}</span>
                      </span>
                    ))}
                  </div>
                )}
                {(it.observations || it.strengths || it.opportunities || it.recommendations) && (
                  <div className="mt-2 space-y-1 border-t border-gray-100 pt-2 text-xs text-gray-600">
                    {it.observations && <p><span className="font-semibold">Observaciones:</span> {it.observations}</p>}
                    {it.strengths && <p><span className="font-semibold">Fortalezas:</span> {it.strengths}</p>}
                    {it.opportunities && <p><span className="font-semibold">Áreas de oportunidad:</span> {it.opportunities}</p>}
                    {it.recommendations && <p><span className="font-semibold">Recomendaciones:</span> {it.recommendations}</p>}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end border-t border-gray-100 px-6 py-4">
          <button onClick={onClose} className="rounded-lg bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default EmpresaEntrevistas
